import { Option } from '@inrev/core';
import { isMatching, P } from 'ts-pattern';
import { z } from 'zod';
import { suretyTypeSchema } from '../../../enums.mjs';
import { actorTypeSchema, actorSystemSourceSchema } from '../../actor/actor.mjs';
import { Currency } from '../../currency/currency.mjs';
import { rateSchemaV2, userId } from '../../opaque.mjs';
import { currencySchema, formatToDollar } from '../../utils.mjs';

const SINGLE_EXCEEDS_AGGREGATE = "Single can't exceed aggregate";
const singleExceedsAggregateMessage = (aggregate) => `${SINGLE_EXCEEDS_AGGREGATE} (${formatToDollar(aggregate)})`;
const positiveRateMessage = "Rate must be above 0%";
const rateTooHighMessage = "Rate must be below 100%";
const negativeLimitMessage = "Limits cannot be negative";
const requiredMessage = (field) => `${field} is required`;
const hasBothLimits = isMatching({ singleLimit: P.number, aggregateLimit: P.number });
const rateSchema = z
    .number({ error: (issue) => (Option.isNone(issue.input) ? requiredMessage("Rate") : undefined) })
    .gt(0, { error: positiveRateMessage })
    .lt(1, { error: rateTooHighMessage })
    .pipe(rateSchemaV2)
    .refine((val) => val > 0, { error: positiveRateMessage })
    .refine((val) => val < 1, { error: rateTooHighMessage });
const limitSchema = (field) => z
    .any()
    .transform((val) => (val === "" || Option.isNone(val) ? undefined : Number(val)))
    .pipe(z.number({
    error: (issue) => Option.isNone(issue.input) ? requiredMessage(field) : "Not a valid number",
}))
    .pipe(currencySchema)
    .refine((val) => val >= 0, {
    error: negativeLimitMessage,
})
    .transform(Currency.round);
const schema = z
    .object({
    rate: rateSchema,
    singleLimit: limitSchema("Single"),
    aggregateLimit: limitSchema("Aggregate"),
})
    .superRefine((line, ctx) => {
    if (line.singleLimit > line.aggregateLimit) {
        ctx.addIssue({
            code: "custom",
            path: ["singleLimit"],
            message: singleExceedsAggregateMessage(line.aggregateLimit),
        });
    }
}, { when: ({ value }) => hasBothLimits(value) });
const linesSchema = z.partialRecord(suretyTypeSchema, schema);
const limitsSchema = z
    .object({
    singleLimit: schema.shape.singleLimit,
    aggregateLimit: schema.shape.aggregateLimit,
})
    .superRefine((limits, ctx) => {
    if (limits.singleLimit > limits.aggregateLimit) {
        ctx.addIssue({
            code: "custom",
            path: ["singleLimit"],
            message: singleExceedsAggregateMessage(limits.aggregateLimit),
        });
    }
}, { when: ({ value }) => hasBothLimits(value) });
const maskedLineSchema = z.object({
    rate: schema.shape.rate.nullable(),
    singleLimit: schema.shape.singleLimit.nullable(),
    aggregateLimit: schema.shape.aggregateLimit.nullable(),
});
const ratesSchema = z.partialRecord(suretyTypeSchema, schema.shape.rate);
var UpdatedBy;
(function (UpdatedBy) {
    UpdatedBy.typeSchema = actorTypeSchema.extract(["user", "system"]);
    UpdatedBy.userSchema = z.object({ type: UpdatedBy.typeSchema.extract(["user"]), userId });
    UpdatedBy.systemSchema = z.object({
        type: UpdatedBy.typeSchema.extract(["system"]),
        source: actorSystemSourceSchema,
    });
    UpdatedBy.schema = z.discriminatedUnion("type", [UpdatedBy.userSchema, UpdatedBy.systemSchema]);
})(UpdatedBy || (UpdatedBy = {}));

export { UpdatedBy, limitsSchema, linesSchema, maskedLineSchema, rateSchema, ratesSchema, schema };
